import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FormRow,
  FormGroup, Label, Input, Select, Button, ButtonGroup, SectionTitle,
  Modal, ModalContent, ModalHeader, CloseButton, Divider,
} from '../../styles/PageStyles';
import ImageCropper from './ImageCropper';
import { put } from '../../utils/apiUtils';
import { register } from '../../services/userService';
import toastService from '../../services/toastService';



const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const PERS_ENDPOINT = `${BASE_URL}/api/v1/persistence/users`;


const UserDetails = ({ user, onSaveUser, selectedGroup }) => {
  const navigate = useNavigate();
  const isNewUser = !user || !user.id;
  const [userDetails, setUserDetails] = useState(user || { 
    first_name: '', 
    last_name: '', 
    gender: 'M', 
    date_of_birth: new Date().toISOString(), 
    company_cd: user?.companyCd || '', 
    status: "INACTIVE", 
    address: '', 
    phone_number: '', 
    bio: '', 
    profile_url: null, 
    username: '', 
    email: '', 
    enabled: true, 
    roles: [], 
    groups: selectedGroup ? [selectedGroup] : [] 
  });
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadedImage] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [roleInput, setRoleInput] = useState('');

  // Update groups when selectedGroup prop changes
  useEffect(() => {
    if (selectedGroup && isNewUser) {
      setUserDetails(prev => ({
        ...prev,
        groups: [selectedGroup]
      }));
    }
  }, [selectedGroup, isNewUser]);

  const showValidationErrors = (error) => {
    if (error.response && error.response.data && error.response.data.detail) {
      const errors = error.response.data.detail;
      errors.forEach(validationError => {
        const fieldName = validationError.loc[validationError.loc.length - 1];
        const message = validationError.msg;
        toastService.error(`${fieldName}: ${message}`);
      });
    } else {
      toastService.error('An error occurred while saving the user');
    }
  };

  const updateProfile = async (profileUpdateFlag = false) => {
    if (profileUpdateFlag) {
      userDetails.profile_url = uploadedFileName;
    }
    try {
      const response = await put(`${PERS_ENDPOINT}`, userDetails, {
        withCredentials: true,
      });
      setUserDetails(response);

    } catch (error) {
      console.error('Error updating profile:', error);
      navigate('/login');
    }
  };

  const saveUser = async () => {
    try {
      let response;
      if (isNewUser) {
        // Prepare payload for register API
        const registerPayload = {
          username: userDetails.username,
          email: userDetails.email,
          first_name: userDetails.first_name,
          last_name: userDetails.last_name,
          enabled: userDetails.enabled,
          profile_url: uploadedFileName || userDetails.profile_url || "",
          phone_number: userDetails.phone_number,
          address: userDetails.address,
          date_of_birth: userDetails.date_of_birth,
          gender: userDetails.gender,
          bio: userDetails.bio,
          roles: userDetails.roles || [],
          groups: userDetails.groups || []
        };
        
        // Call register API for new users
        response = await register(registerPayload);
        toastService.success('User registered successfully');
      } else {
        // Update existing user profile
        if (uploadedFileName) {
          userDetails.profile_url = uploadedFileName;
        }
        response = await put(`${PERS_ENDPOINT}`, userDetails, {
          withCredentials: true,
        });
        toastService.success('User updated successfully');
      }
      
      if (onSaveUser) {
        onSaveUser(response);
      }
    } catch (error) {
      console.error(`Error ${isNewUser ? 'registering' : 'updating'} user:`, error);
      showValidationErrors(error);
      if (!isNewUser && error.response?.status === 401) {
        navigate('/login');
      }
    }
  };


  const handleCropComplete = (croppedFile) => {
    console.log('Uploaded file name:', croppedFile);

  };
  const handleFileUpload = (fileName) => {
    // console.log('handleFileUpload called with:', fileName);
    setUploadedFileName(fileName);
    // console.log('uploadedFileName after setState:', fileName);
    updateProfile(true);
  };

  const addRole = () => {
    if (roleInput.trim() && !(userDetails.roles || []).includes(roleInput.trim())) {
      setUserDetails({
        ...userDetails,
        roles: [...(userDetails.roles || []), roleInput.trim()]
      });
      setRoleInput('');
    }
  };

  const removeRole = (roleToRemove) => {
    setUserDetails({
      ...userDetails,
      roles: (userDetails.roles || []).filter(role => role !== roleToRemove)
    });
  };

  const handleRoleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addRole();
    }
  };

  return (
    <div>
      <SectionTitle>
        {userDetails ? (
          <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
            Edit {userDetails.first_name} {userDetails.last_name}
          </span>
        ) : (
          <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
            Add User
          </span>
        )}
      </SectionTitle>
      <Divider />

      <FormRow>
        <FormGroup>
          <Label>First Name</Label>
          <Input type="text" value={userDetails?.first_name || ''}
            onChange={(e) => setUserDetails({ ...userDetails, first_name: e.target.value })}
          />

        </FormGroup>
        <FormGroup>
          <Label>Last Name</Label>
          <Input type="text" value={userDetails?.last_name || ''}
            onChange={(e) => setUserDetails({ ...userDetails, last_name: e.target.value })} />
        </FormGroup>
      </FormRow>
      <FormRow>
        <FormGroup>
          <Label>Gender</Label>
          <Select
            value={userDetails?.gender || ''}
            onChange={(e) => setUserDetails({ ...userDetails, gender: e.target.value })}
          >
            <option value="">Select Gender</option>
            <option value="M">Male</option>
            <option value="F">Female</option>
            <option value="Other">Other</option>
          </Select>

        </FormGroup>
        <FormGroup>
          <Label>Date of Birth</Label>
          <Input
            type="date"
            value={userDetails?.date_of_birth ? userDetails.date_of_birth.split('T')[0] : ''}
            onChange={(e) => {
              const selectedDate = e.target.value;
              const formattedDate = `${selectedDate}T00:00:00Z`;
              setUserDetails({ ...userDetails, date_of_birth: formattedDate });
            }}
          />
        </FormGroup>
      </FormRow>
      <FormRow>
        <FormGroup>
          <Label>Email Address</Label>
          <Input type="email" value={userDetails?.email || ''} onChange={(e) => setUserDetails({ ...userDetails, email: e.target.value })} />
        </FormGroup>

        <FormGroup>
          <Label>Phone</Label>
          <Input type="text" value={userDetails?.phone_number || ''} onChange={(e) => setUserDetails({ ...userDetails, phone_number: e.target.value })} />
        </FormGroup>
      </FormRow>

      {isNewUser && (
        <FormRow>
          <FormGroup>
            <Label>Username</Label>
            <Input type="text" value={userDetails?.username || ''} onChange={(e) => setUserDetails({ ...userDetails, username: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label>Roles</Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Input 
                  type="text" 
                  value={roleInput} 
                  onChange={(e) => setRoleInput(e.target.value)}
                  onKeyPress={handleRoleKeyPress}
                  placeholder="Enter role and press Enter"
                  style={{ flex: 1 }}
                />
                <Button 
                  type="button"
                  onClick={addRole}
                  style={{ minWidth: '60px', fontSize: '12px', padding: '4px 8px' }}
                >
                  Add
                </Button>
              </div>
              {userDetails.roles && userDetails.roles.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {userDetails.roles.map((role, index) => (
                    <span 
                      key={index}
                      style={{
                        background: '#e3f2fd',
                        border: '1px solid #1976d2',
                        borderRadius: '16px',
                        padding: '4px 12px',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      {role}
                      <button
                        type="button"
                        onClick={() => removeRole(role)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#1976d2',
                          cursor: 'pointer',
                          fontSize: '14px',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </FormGroup>
        </FormRow>
      )}

      {isNewUser && selectedGroup && (
        <FormRow>
          <FormGroup>
            <Label>Group</Label>
            <div style={{
              background: '#f5f5f5',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '8px 12px',
              color: '#666',
              fontStyle: 'italic'
            }}>
              {selectedGroup} (selected from previous screen)
            </div>
          </FormGroup>
        </FormRow>
      )}

      <FormRow>
        <FormGroup>
          <Label>Address</Label>
          <Input type="text" value={userDetails?.address || ''} onChange={(e) => setUserDetails({ ...userDetails, address: e.target.value })} />
        </FormGroup>

      </FormRow>
      <FormGroup>
        <Label>Profile Image</Label>
        <Button
          secondary
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowProfileModal(true)} // Open modal on click
        >
          Upload Profile Picture
        </Button>
        {showProfileModal && (
          <Modal>
            <ModalContent>
              <ModalHeader>
                <h3>Upload Profile Picture</h3>
                <CloseButton onClick={() => setShowProfileModal(false)}>✖</CloseButton>
              </ModalHeader>
              <Divider />
              <ImageCropper
                uploadedImage={uploadedImage}
                onCropComplete={handleCropComplete}
                onFileUpload={handleFileUpload}
              />
            </ModalContent>
          </Modal>
        )}
      </FormGroup>
      <ButtonGroup>


        <Button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Cancel
        </Button>
        <Button onClick={saveUser}
          primary
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {isNewUser ? 'Register User' : 'Save Changes'}
        </Button>
      </ButtonGroup>



    </div>
  );
};

export default UserDetails;